import React, { useState, useEffect, useRef } from 'react';

const emptyCustomer = {
  id: '',
  name: '',
  email: '',
  type: 'company',
  status: '見込み',
  memo: '',
  fields: [],
};

const statusOptions = ['見込み', '商談中', '契約済み', '休止'];
const typeLabels = { company: '企業', sr: '社労士' };

const CRMAdmin = ({ filter = 'all' }) => {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyCustomer);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [sortKey, setSortKey] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);
  const fileInputRef = useRef(null);

  const [events, setEvents] = useState([]);

  const fetchData = () => {
    fetch('/data/customers.json?t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setCustomers)
      .catch(() => setCustomers([]));
  };

  useEffect(() => {
    fetchData();
    fetch('/data/events.json?t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setEvents).catch(() => setEvents([]));
  }, []);

  const getEventName = (id) => {
    const ev = events.find((e) => e.id === id);
    return ev ? ev.name : id;
  };

  const save = async (data) => {
    setSaving(true);
    try {
      const res = await fetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'customers', data }),
      });
      const result = await res.json();
      if (result.success) {
        setCustomers(data);
        setMessage('保存しました');
        setTimeout(() => setMessage(''), 2000);
      }
    } catch {}
    setSaving(false);
  };

  // フォーム用ヘルパー: 姓名・会社名・電話番号をfieldsから取り出し
  const getFormField = (f, key) => (f.fields || []).find((x) => x.key === key)?.value || '';
  const setFormField = (key, value) => {
    setForm((prev) => {
      const fields = (prev.fields || []).filter((x) => x.key !== key);
      if (value) fields.push({ key, value });
      return { ...prev, fields };
    });
  };
  const formSei = form.name ? form.name.split(/\s+/)[0] || '' : '';
  const formMei = form.name ? form.name.split(/\s+/).slice(1).join(' ') || '' : '';
  const setFormName = (sei, mei) => setForm((prev) => ({ ...prev, name: (sei + ' ' + mei).trim() }));

  const startNew = () => {
    setForm({ ...emptyCustomer, id: Date.now() });
    setEditing(true);
    setSelected(null);
  };

  const startEdit = () => {
    if (!selected) return;
    setForm({ ...selected, fields: [...(selected.fields || [])] });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm(emptyCustomer);
  };

  const saveCustomer = async () => {
    let updated;
    if (customers.find((c) => c.id === form.id)) {
      updated = customers.map((c) => c.id === form.id ? form : c);
    } else {
      updated = [...customers, form];
    }
    await save(updated);
    setEditing(false);
    setSelected(form);
    setForm(emptyCustomer);
  };

  const deleteCustomer = async () => {
    if (!selected || !window.confirm(`${selected.name} を削除しますか？`)) return;
    const updated = customers.filter((c) => c.id !== selected.id);
    await save(updated);
    setSelected(null);
  };

  const addField = () => {
    setForm((prev) => ({
      ...prev,
      fields: [...prev.fields, { key: '', value: '' }],
    }));
  };

  const updateField = (index, prop, value) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((f, i) => i === index ? { ...f, [prop]: value } : f),
    }));
  };

  const removeField = (index) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
  };

  // Excelインポート（PHP API経由）
  const handleExcelFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('action', 'preview');
    try {
      const res = await fetch('/api/admin-import.php', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setImportPreview({ data: data.parsed, totalCount: data.totalCount, skippedList: data.skipped, fileName: file.name, file });
      } else {
        setMessage(data.message || 'インポートに失敗しました');
      }
    } catch { setMessage('インポートに失敗しました'); }
    e.target.value = '';
  };

  const executeImport = async () => {
    if (!importPreview) return;
    const formData = new FormData();
    formData.append('file', importPreview.file);
    formData.append('action', 'import');
    try {
      const res = await fetch('/api/admin-import.php', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setMessage(`${data.importedCount}件をインポートしました${data.skippedCount > 0 ? `（重複スキップ: ${data.skippedCount}件）` : ''}`);
        fetchData();
      } else {
        setMessage(data.message || 'インポートに失敗しました');
      }
    } catch { setMessage('インポートに失敗しました'); }
    setImportPreview(null);
  };

  // フィルター
  const filtered = customers.filter((c) => {
    if (filter === 'セミナー参加者' && c.type !== 'sr') return false;
    if (filter === 'アカデミー関連') {
      const hasAcademy = (c.memo || '').includes('アカデミー') || (c.fields || []).some((f) => f.value.includes('アカデミー'));
      if (!hasAcademy && c.status !== '商談中') return false;
    }
    if (filter === 'パートナー候補' && c.type !== 'sr') return false;
    if (filter === '問い合わせ中' && c.status !== '見込み') return false;
    if (filter === '対応済み' && c.status !== '契約済み') return false;
    if (search) {
      const s = search.toLowerCase();
      return c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s);
    }
    return true;
  });

  // ソート
  const getField = (c, key) => (c.fields || []).find((f) => f.key === key)?.value || '';
  const mlPatterns = [...new Set(customers.map((c) => getField(c, 'MLパターン')).filter(Boolean))].sort();

  const sorted = [...filtered].sort((a, b) => {
    if (!sortKey) return 0;
    let va, vb;
    if (sortKey === 'name') { va = getField(a, 'フリガナ') || a.name || ''; vb = getField(b, 'フリガナ') || b.name || ''; }
    else if (sortKey === 'company') { va = getField(a, '会社名'); vb = getField(b, '会社名'); }
    else if (sortKey === 'type') { va = a.type || ''; vb = b.type || ''; }
    else if (sortKey === 'status') { va = a.status || ''; vb = b.status || ''; }
    else if (sortKey === 'pattern') { va = getField(a, 'MLパターン'); vb = getField(b, 'MLパターン'); }
    else return 0;
    const cmp = va.localeCompare(vb, 'ja');
    return sortAsc ? cmp : -cmp;
  });

  const toggleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const sortIcon = (key) => sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '';

  const statusColors = {
    '見込み': 'bg-yellow-100 text-yellow-700',
    '商談中': 'bg-blue-100 text-blue-700',
    '契約済み': 'bg-green-100 text-green-700',
    '休止': 'bg-gray-100 text-gray-500',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">顧客管理</h2>
        <div className="flex gap-2">
          <label className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-green-700 cursor-pointer">
            Excelインポート
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleExcelFile} />
          </label>
          <button onClick={startNew} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">
            + 新規登録
          </button>
        </div>
      </div>

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      {/* インポートプレビュー */}
      {importPreview && (
        <div className="mb-4 bg-white rounded-xl shadow p-6">
          <h3 className="font-bold text-lg mb-3">インポートプレビュー: {importPreview.fileName}</h3>
          <div className="flex gap-4 mb-3 text-sm">
            <span className="font-bold text-blue-700">取り込み: {(importPreview.totalCount || importPreview.data.length)}件</span>
            {importPreview.skippedList.length > 0 && <span className="font-bold text-orange-600">重複スキップ: {importPreview.skippedList.length}件</span>}
          </div>
          <div className="max-h-60 overflow-y-auto border rounded-lg mb-4">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">名前</th>
                  <th className="text-left px-3 py-2">メール</th>
                  <th className="text-left px-3 py-2">区分</th>
                  <th className="text-left px-3 py-2">会社名</th>
                  <th className="text-left px-3 py-2">MLパターン</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {importPreview.data.slice(0, 50).map((c, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5">{c.name}</td>
                    <td className="px-3 py-1.5">{c.email}</td>
                    <td className="px-3 py-1.5">{c.type === 'sr' ? '社労士' : '企業'}</td>
                    <td className="px-3 py-1.5">{c.fields.find((f) => f.key === '会社名')?.value || ''}</td>
                    <td className="px-3 py-1.5">{c.fields.find((f) => f.key === 'MLパターン')?.value || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(importPreview.totalCount || importPreview.data.length) > 50 && <p className="text-center text-xs text-gray-400 py-2">他 {(importPreview.totalCount || importPreview.data.length) - 50}件...</p>}
          </div>
          {/* スキップ一覧 */}
          {importPreview.skippedList.length > 0 && (
            <details className="mb-4">
              <summary className="text-sm font-bold text-orange-600 cursor-pointer hover:underline">
                スキップされた {importPreview.skippedList.length}件を表示
              </summary>
              <div className="mt-2 max-h-40 overflow-y-auto border border-orange-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-orange-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">名前</th>
                      <th className="text-left px-3 py-2">メール</th>
                      <th className="text-left px-3 py-2">理由</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {importPreview.skippedList.map((s, i) => (
                      <tr key={i}>
                        <td className="px-3 py-1.5">{s.name}</td>
                        <td className="px-3 py-1.5">{s.email}</td>
                        <td className="px-3 py-1.5 text-orange-600">{s.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
          <div className="flex gap-3">
            <button onClick={executeImport} disabled={saving} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'インポート中...' : `${(importPreview.totalCount || importPreview.data.length)}件をインポート`}
            </button>
            <button onClick={() => setImportPreview(null)} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* 検索 */}
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="名前・メールアドレスで検索"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex gap-6">
        {/* 顧客一覧 */}
        <div className="w-1/2">
          <div className="mb-1 text-right text-xs text-gray-400 font-bold">{sorted.length}件</div>
          <div className="bg-white rounded-xl shadow overflow-hidden">
            {sorted.length === 0 ? (
              <div className="p-8 text-center text-gray-500">顧客がいません</div>
            ) : (
              <div className="max-h-[70vh] overflow-y-auto">
                <table className="w-full table-fixed">
                  <colgroup>
                    <col style={{width:'30%'}} />
                    <col style={{width:'28%'}} />
                    <col style={{width:'14%'}} />
                    <col style={{width:'16%'}} />
                    <col style={{width:'12%'}} />
                  </colgroup>
                  <thead className="bg-gray-50 border-b sticky top-0">
                    <tr>
                      {[
                        { key: 'name', label: '名前' },
                        { key: 'company', label: '会社名' },
                        { key: 'type', label: '区分' },
                        { key: 'status', label: 'ステータス' },
                        { key: 'pattern', label: 'ML' },
                      ].map((s) => (
                        <th key={s.key} onClick={() => toggleSort(s.key)}
                          className={`text-left px-2 py-2 text-[11px] font-bold cursor-pointer select-none hover:bg-gray-100 ${sortKey === s.key ? 'text-blue-700' : 'text-gray-500'}`}>
                          {s.label}{sortIcon(s.key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sorted.map((customer) => (
                      <tr key={customer.id} onClick={() => { setSelected(customer); setEditing(false); }}
                        className={`cursor-pointer hover:bg-gray-50 transition-colors ${selected?.id === customer.id ? 'bg-blue-50' : ''}`}>
                        <td className="px-2 py-2">
                          <p className="font-bold text-xs truncate">{customer.name}</p>
                          <p className="text-[10px] text-gray-400 truncate">{customer.email}</p>
                        </td>
                        <td className="px-2 py-2 text-xs text-gray-600 truncate">{getField(customer, '会社名')}</td>
                        <td className="px-2 py-1.5">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${customer.type === 'sr' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {typeLabels[customer.type] || customer.type}
                          </span>
                        </td>
                        <td className="px-2 py-1.5">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${statusColors[customer.status] || 'bg-gray-100 text-gray-600'}`}>
                            {customer.status}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-[10px] text-gray-500 font-bold">{getField(customer, 'MLパターン')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* 詳細・編集 */}
        <div className="w-1/2">
          {editing ? (
            <div className="bg-white rounded-xl shadow p-6 space-y-4">
              <h3 className="font-bold text-lg">{form.id && customers.find((c) => c.id === form.id) ? '編集' : '新規登録'}</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-1">姓 *</label>
                  <input type="text" value={formSei} onChange={(e) => setFormName(e.target.value, formMei)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">名</label>
                  <input type="text" value={formMei} onChange={(e) => setFormName(formSei, e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold mb-1">会社名</label>
                <input type="text" value={getFormField(form, '会社名')} onChange={(e) => setFormField('会社名', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-1">メールアドレス</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">電話番号</label>
                  <input type="text" value={getFormField(form, '電話番号')} onChange={(e) => setFormField('電話番号', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-1">区分</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="company">企業</option>
                    <option value="sr">社労士</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">ステータス</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">MLパターン</label>
                  <select value={(form.fields || []).find((f) => f.key === 'MLパターン')?.value || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm((prev) => {
                        const fields = prev.fields.filter((f) => f.key !== 'MLパターン');
                        if (val) fields.push({ key: 'MLパターン', value: val });
                        return { ...prev, fields };
                      });
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">なし</option>
                    {mlPatterns.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              {/* 自由フィールド */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-bold">追加項目</label>
                  <button onClick={addField} className="text-xs text-blue-600 font-bold hover:underline">+ 項目追加</button>
                </div>
                {form.fields.filter((f) => !['会社名', '電話番号', 'MLパターン'].includes(f.key)).map((field, i) => {
                  const realIndex = form.fields.indexOf(field);
                  return (
                  <div key={realIndex} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={field.key}
                      onChange={(e) => updateField(realIndex, 'key', e.target.value)}
                      placeholder="項目名"
                      className="w-1/3 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={field.value}
                      onChange={(e) => updateField(realIndex, 'value', e.target.value)}
                      placeholder="値"
                      className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button onClick={() => removeField(realIndex)} className="text-red-500 text-xs font-bold hover:text-red-700">削除</button>
                  </div>
                  );
                })}
              </div>

              <div>
                <label className="block text-sm font-bold mb-1">メモ</label>
                <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>

              <div className="flex gap-3">
                <button onClick={saveCustomer} disabled={saving || !form.name}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                  {saving ? '保存中...' : '保存'}
                </button>
                <button onClick={cancelEdit} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">
                  キャンセル
                </button>
              </div>
            </div>
          ) : selected ? (
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg">{selected.name}</h3>
                  <p className="text-sm text-gray-500">{selected.email}</p>
                  <div className="flex gap-1.5 mt-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${selected.type === 'sr' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {typeLabels[selected.type] || selected.type}
                    </span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${statusColors[selected.status] || 'bg-gray-100 text-gray-600'}`}>
                      {selected.status}
                    </span>
                    {getField(selected, 'MLパターン') && (
                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-orange-100 text-orange-700">
                        ML: {getField(selected, 'MLパターン')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={startEdit} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-200">編集</button>
                  <button onClick={deleteCustomer} className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100">削除</button>
                </div>
              </div>

              {/* 追加項目 */}
              {selected.fields && selected.fields.length > 0 && (
                <div className="border-t pt-4 mb-4">
                  <div className="space-y-2">
                    {selected.fields.map((field, i) => (
                      <div key={i} className="flex gap-2 text-sm">
                        <span className="font-bold text-gray-600 shrink-0">{field.key === 'イベントID' ? 'イベント' : field.key}:</span>
                        <span className="text-gray-700">{field.key === 'イベントID' ? getEventName(field.value) : field.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* メモ */}
              {selected.memo && (
                <div className="border-t pt-4">
                  <p className="text-sm font-bold text-gray-600 mb-1">メモ</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{selected.memo}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
              左の一覧から顧客を選択してください
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CRMAdmin;
